import { PersonalInboxPage } from "../features/personal/PersonalPages";
import { workspaceExperience } from "../lib/workspaceExperience";
import { lazy, Suspense, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, Route, Routes, useLocation, useParams, useNavigate } from "react-router-dom";
import { loginDestination } from "../features/auth/returnTo";
import { AppShell } from "../components/AppShell";
import { isInboxOnlyWebUser, requiresOnboarding } from "../features/auth/flow";
import { api } from "../lib/api";
import { useSession } from "../lib/session";
import { tabAccess } from "../lib/tabAccess";
import type { OrganizationTabCode } from "../lib/types";

const LoginPage = lazy(() => import("../features/auth/LoginPage"));
const OnboardingPage = lazy(() => import("../features/auth/OnboardingPage"));
const HomePage = lazy(() => import("../features/home/HomePage"));
const AgencyInboxPage = lazy(() => import("../features/inbox/AgencyInboxPage"));
const ProductionWorkspace = lazy(() => import("../features/inbox/ProductionWorkspace"));
const CreateProjectOrderPage = lazy(() => import("../features/projects/CreateProjectOrderPage"));
const ProcurementPage = lazy(() => import("../features/procurement/ProcurementPage"));
const OrganizationDetailsPage = lazy(() => import("../features/settings/OrganizationDetailsPage"));
const ProjectsPage = lazy(() => import("../features/projects/ProjectsPage"));
const ClientProjectsPage = lazy(() => import("../features/projects/ClientProjectsPage"));
const ProjectDetailsPage = lazy(() => import("../features/projects/ProjectPage"));
const CreateCatalogueProjectPage = lazy(() => import("../features/projects/CreateCatalogueProjectPage"));
const RateCardsPage = lazy(() => import("../features/rate-cards/RateCardsPage"));
const CreateRateCardPage = lazy(() => import("../features/rate-cards/CreateRateCardPage"));
const RateCardDetailPage = lazy(() => import("../features/rate-cards/RateCardDetailPage"));
const LocationsPage = lazy(() => import("../features/locations/LocationsPage"));
const AssetsPage = lazy(() => import("../features/assets/AssetsPage"));
const TasksPage = lazy(() => import("../features/tasks/TasksPage"));
const TaskDetailsPage = lazy(() => import("../features/tasks/TaskDetailsPage"));
const CataloguePage = lazy(() => import("../features/catalogue/CataloguePage"));
const CrmPage = lazy(() => import("../features/crm/CrmPage"));
const UsersPage = lazy(() => import("../features/users/UsersPage"));
const StaffReportingPage = lazy(() => import("../features/staff-reporting/StaffReportingPage"));
const LeaveRequestsPage = lazy(() => import("../features/leaves/LeaveRequestsPage"));
const DutyStatusPage = lazy(() => import("../features/duty-status/DutyStatusPage"));
const SettingsPage = lazy(() => import("../features/settings/SettingsPage"));
function LegacyProductionTaskRedirect() {
  const { taskId = "" } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/tasks/${taskId}${search || "?type=PRODUCTION"}`} replace />;
}


function LoadingScreen() {
  return (
    <div className="loading-screen">
      <div className="skeleton-line wide" />
      <div className="skeleton-grid">
        <div />
        <div />
        <div />
      </div>
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { accessToken } = useSession();
  const location = useLocation();
  if (!accessToken) {
    return <Navigate to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search + location.hash)}`} replace />;
  }
  return children;
}

function PublicOnly({ children }: { children: React.ReactNode }) {
  const { accessToken } = useSession();
  const location = useLocation();
  if (accessToken) {
    return <Navigate to={loginDestination(location.search, location.state)} replace />;
  }
  return children;
}

function RequireCompletedOnboarding({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const cache = useQueryClient();
  const navigate = useNavigate();
  useEffect(() => {
    const revoked = () => { cache.clear(); navigate("/personal-inbox", { replace: true }); };
    window.addEventListener("organization-access-changed", revoked);
    return () => window.removeEventListener("organization-access-changed", revoked);
  }, [cache, navigate]);
  const contextQuery = useQuery({
    queryKey: ["user-context"],
    queryFn: api.getUserContext,
    staleTime: 0
  });

  if (contextQuery.isLoading) {
    return <LoadingScreen />;
  }
  if (contextQuery.isError || !contextQuery.data) {
    return (
      <div className="login-page">
        <section className="login-card workspace-gate-error">
          <h1>We could not load your workspace</h1>
          <p>Check the backend connection, then try again.</p>
          <button type="button" className="primary-action full" onClick={() => contextQuery.refetch()}>Try again</button>
        </section>
      </div>
    );
  }
  if (contextQuery.data.context_status === "LOGGED_IN_USER_WITH_ORG_PENDING_APPROVAL" || (contextQuery.data.personal_access && !contextQuery.data.active_organization)) {
    return <Navigate to="/personal-inbox" replace />;
  }
  if (requiresOnboarding(contextQuery.data.context_status)) {
    return <Navigate to={`/onboarding?returnTo=${encodeURIComponent(location.pathname + location.search + location.hash)}`} replace />;
  }
  return children;
}

function RequireFullWebWorkspace({ children }: { children: React.ReactNode }) {
  const contextQuery = useQuery({
    queryKey: ["user-context"],
    queryFn: api.getUserContext,
    staleTime: 0
  });

  if (contextQuery.isLoading) {
    return <LoadingScreen />;
  }
  if (!workspaceExperience(contextQuery.data) && isInboxOnlyWebUser(contextQuery.data?.context_status)) {
    return <Navigate to={contextQuery.data?.context_status === "LOGGED_IN_PRODUCTION_TEAM_USER" ? "/production" : "/inbox"} replace />;
  }
  return children;
}

function RequireFullOrProductionManagerWorkspace({ children }: { children: React.ReactNode }) {
  return <RequireFullWebWorkspace>{children}</RequireFullWebWorkspace>;
}

function RequireProductionWorkspace({
  managerOnly = false,
  children
}: {
  managerOnly?: boolean;
  children: React.ReactNode;
}) {
  const contextQuery = useQuery({
    queryKey: ["user-context"],
    queryFn: api.getUserContext,
    staleTime: 0
  });
  const experience = workspaceExperience(contextQuery.data);
  const isProductionTeam = !experience && contextQuery.data?.context_status === "LOGGED_IN_PRODUCTION_TEAM_USER";
  const hasOrganization = Boolean(contextQuery.data?.active_organization);
  const shouldCheckProductionManager = managerOnly && (isProductionTeam || Boolean(experience));
  const workspaceQuery = useQuery({
    queryKey: ["production-workspace"],
    queryFn: () => api.getProductionWorkspace(),
    enabled: shouldCheckProductionManager
  });

  if (contextQuery.isLoading || (shouldCheckProductionManager && workspaceQuery.isLoading)) {
    return <LoadingScreen />;
  }
  if (!experience && !isProductionTeam && isInboxOnlyWebUser(contextQuery.data?.context_status)) {
    return <Navigate to="/inbox" replace />;
  }
  if (!isProductionTeam && !hasOrganization) {
    return <Navigate to="/" replace />;
  }
  if (shouldCheckProductionManager && !workspaceQuery.data?.can_manage) {
    return <Navigate to="/production" replace />;
  }
  return children;
}

function RequireTabAccess({
  tabCode,
  update = false,
  children
}: {
  tabCode: OrganizationTabCode;
  update?: boolean;
  children: React.ReactNode;
}) {
  const contextQuery = useQuery({
    queryKey: ["user-context"],
    queryFn: api.getUserContext,
    staleTime: 0
  });
  if (contextQuery.isLoading) {
    return <LoadingScreen />;
  }
  const permission = tabAccess(contextQuery.data, tabCode);
  if (!permission.can_view || (update && !permission.can_update)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function WorkspaceIndex() {
  const contextQuery = useQuery({
    queryKey: ["user-context"],
    queryFn: api.getUserContext,
    staleTime: 0
  });

  if (contextQuery.isLoading) {
    return <LoadingScreen />;
  }
  const experience = workspaceExperience(contextQuery.data);
  if (experience) return experience.defaultRoute === "/tasks" ? <Navigate to="/tasks" replace /> : <HomePage />;
  if (contextQuery.data?.context_status === "LOGGED_IN_PRODUCTION_TEAM_USER") {
    return <Navigate to="/production" replace />;
  }
  if (!workspaceExperience(contextQuery.data) && isInboxOnlyWebUser(contextQuery.data?.context_status)) {
    return <Navigate to="/inbox" replace />;
  }
  return <HomePage />;
}

export function App() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
        <Route path="/personal-inbox" element={<RequireAuth><PersonalInboxPage /></RequireAuth>} />
        <Route path="/business-links" element={<RequireAuth><Navigate to="/personal-inbox" replace /></RequireAuth>} />
        <Route path="/onboarding" element={<RequireAuth><OnboardingPage /></RequireAuth>} />
        <Route path="/" element={<RequireAuth><RequireCompletedOnboarding><AppShell /></RequireCompletedOnboarding></RequireAuth>}>
          <Route index element={<WorkspaceIndex />} />
          <Route path="inbox" element={<AgencyInboxPage />} />
          <Route path="inbox/:taskId" element={<AgencyInboxPage />} />
          <Route path="production" element={<RequireProductionWorkspace><ProductionWorkspace section="PRODUCTION" /></RequireProductionWorkspace>} />
          <Route path="delivery" element={<RequireProductionWorkspace managerOnly><ProductionWorkspace section="DELIVERY" /></RequireProductionWorkspace>} />
          <Route path="projects" element={<RequireFullOrProductionManagerWorkspace><ClientProjectsPage /></RequireFullOrProductionManagerWorkspace>} />
          <Route path="projects/create" element={<RequireFullWebWorkspace><CreateProjectOrderPage /></RequireFullWebWorkspace>} />
          <Route path="my-work/projects" element={<RequireFullOrProductionManagerWorkspace><ProjectsPage direction="MY_WORK" /></RequireFullOrProductionManagerWorkspace>} />
          <Route path="my-work/projects/create" element={<RequireFullWebWorkspace><CreateProjectOrderPage direction="MY_WORK" /></RequireFullWebWorkspace>} />
          <Route path="procurement/:section" element={<RequireFullWebWorkspace><ProcurementPage /></RequireFullWebWorkspace>} />
          <Route path="organization/:section" element={<RequireFullWebWorkspace><RequireTabAccess tabCode="SETTINGS"><OrganizationDetailsPage /></RequireTabAccess></RequireFullWebWorkspace>} />
          <Route path="projects/:projectId/edit" element={<RequireFullWebWorkspace><CreateCatalogueProjectPage /></RequireFullWebWorkspace>} />
          <Route path="projects/:projectId" element={<RequireFullOrProductionManagerWorkspace><ProjectDetailsPage /></RequireFullOrProductionManagerWorkspace>} />
          <Route path="ownership" element={<RequireFullWebWorkspace><Navigate to="/projects?tab=ownership" replace /></RequireFullWebWorkspace>} />
          <Route path="locations" element={<RequireFullWebWorkspace><LocationsPage /></RequireFullWebWorkspace>} />
          <Route path="locations/:locationId/assets" element={<RequireFullWebWorkspace><AssetsPage /></RequireFullWebWorkspace>} />
          <Route path="assets" element={<RequireFullWebWorkspace><AssetsPage /></RequireFullWebWorkspace>} />
          <Route path="tasks" element={<RequireFullWebWorkspace><TasksPage /></RequireFullWebWorkspace>} />
          <Route path="tasks/:taskId" element={<TaskDetailsPage />} />
          <Route path="rate-cards" element={<RequireFullWebWorkspace><RequireTabAccess tabCode="RATE_CARDS"><RateCardsPage /></RequireTabAccess></RequireFullWebWorkspace>} />
          <Route path="rate-cards/create" element={<RequireFullWebWorkspace><RequireTabAccess tabCode="RATE_CARDS" update><CreateRateCardPage /></RequireTabAccess></RequireFullWebWorkspace>} />
          <Route path="rate-cards/:rateCardId" element={<RequireFullWebWorkspace><RequireTabAccess tabCode="RATE_CARDS"><RateCardDetailPage /></RequireTabAccess></RequireFullWebWorkspace>} />
          <Route path="catalogue" element={<RequireFullWebWorkspace><CataloguePage /></RequireFullWebWorkspace>} />
          <Route path="connections" element={<Navigate to="/customers" replace />} />
          <Route path="customers" element={<RequireFullWebWorkspace><RequireTabAccess tabCode="CRM"><CrmPage /></RequireTabAccess></RequireFullWebWorkspace>} />
          <Route path="customers/new" element={<RequireFullWebWorkspace><RequireTabAccess tabCode="CRM"><CrmPage /></RequireTabAccess></RequireFullWebWorkspace>} />
          <Route path="customers/:partyId" element={<RequireFullWebWorkspace><RequireTabAccess tabCode="CRM"><CrmPage /></RequireTabAccess></RequireFullWebWorkspace>} />
          <Route path="vendors" element={<RequireFullWebWorkspace><RequireTabAccess tabCode="CRM"><CrmPage /></RequireTabAccess></RequireFullWebWorkspace>} />
          <Route path="vendors/new" element={<RequireFullWebWorkspace><RequireTabAccess tabCode="CRM"><CrmPage /></RequireTabAccess></RequireFullWebWorkspace>} />
          <Route path="vendors/:partyId" element={<RequireFullWebWorkspace><RequireTabAccess tabCode="CRM"><CrmPage /></RequireTabAccess></RequireFullWebWorkspace>} />

          <Route path="users" element={<RequireFullWebWorkspace><RequireTabAccess tabCode="USERS"><UsersPage /></RequireTabAccess></RequireFullWebWorkspace>} />
          <Route path="staff-reporting" element={<RequireFullWebWorkspace><RequireTabAccess tabCode="USERS"><StaffReportingPage /></RequireTabAccess></RequireFullWebWorkspace>} />
          <Route path="leave-requests" element={<LeaveRequestsPage />} />
          <Route path="duty-status" element={<RequireFullWebWorkspace><RequireTabAccess tabCode="DUTY_STATUS"><DutyStatusPage /></RequireTabAccess></RequireFullWebWorkspace>} />
          <Route path="settings" element={<RequireFullWebWorkspace><RequireTabAccess tabCode="SETTINGS"><SettingsPage /></RequireTabAccess></RequireFullWebWorkspace>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
